import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SUBANTA_LINGA, SUBANTA_PRAKAR, enumToOptions } from '../constants/subantaEnums';
import {
  buildSubantaRupFormulaPatch,
  createBodyFromFormValues,
  createSubantaRupFormula,
  deleteSubantaRupFormula,
  formulaToFormValues,
  getSubantaRupFormula,
  listSubantaRupFormulas,
  updateSubantaRupFormula,
  type SubantaRupFormula,
  type SubantaRupFormulaFormValues,
} from '../api/subantaRupFormulaApi';

const prakarOptions = enumToOptions(SUBANTA_PRAKAR);
const lingaOptions = enumToOptions(SUBANTA_LINGA);

const defaultCreateValues: Partial<SubantaRupFormulaFormValues> = {
  prakar: SUBANTA_PRAKAR[0],
  linga: SUBANTA_LINGA[0],
  natwa: false,
  shatwa: false,
  roop_prakar: undefined,
  aadhar_shabda: undefined,
  vishesh: undefined,
  bat: undefined,
};

export function SubantaRupFormulaPage() {
  const [rows, setRows] = useState<SubantaRupFormula[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SubantaRupFormula | null>(null);
  const [saving, setSaving] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [form] = Form.useForm<SubantaRupFormulaFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listSubantaRupFormulas());
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(
        ax?.response?.data?.message || ax?.message || 'Failed to load subanta rup formulas'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(defaultCreateValues as SubantaRupFormulaFormValues);
    setModalOpen(true);
  };

  const openEdit = async (record: SubantaRupFormula) => {
    setEditing(record);
    form.resetFields();
    setModalOpen(true);
    setPrefillLoading(true);
    try {
      const fresh = await getSubantaRupFormula(record.id);
      setEditing(fresh);
      form.setFieldsValue(formulaToFormValues(fresh));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load formula');
      setModalOpen(false);
      setEditing(null);
    } finally {
      setPrefillLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const patch = buildSubantaRupFormulaPatch(editing, values);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateSubantaRupFormula(editing.id, patch);
        message.success('Formula updated');
      } else {
        await createSubantaRupFormula(createBodyFromFormValues(values));
        message.success('Formula created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSubantaRupFormula(id);
      message.success('Formula deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<SubantaRupFormula> = [
      { title: 'ID', dataIndex: 'id', width: 72, fixed: 'left' },
      { title: 'Prakar', dataIndex: 'prakar', width: 140, ellipsis: true },
      { title: 'Linga', dataIndex: 'linga', width: 160, ellipsis: true },
      { title: 'Roop prakar', dataIndex: 'roop_prakar', width: 120, ellipsis: true },
      { title: 'Aadhar shabda', dataIndex: 'aadhar_shabda', width: 120, ellipsis: true },
      {
        title: 'Vishesh',
        dataIndex: 'vishesh',
        ellipsis: true,
        render: (v: string | null | undefined) =>
          v == null || v === '' ? '—' : v.length > 40 ? `${v.slice(0, 40)}…` : v,
      },
      {
        title: 'Bat',
        dataIndex: 'bat',
        width: 80,
        render: (v: string | null | undefined) => (v == null || v === '' ? '—' : v),
      },
      {
        title: 'Natwa',
        dataIndex: 'natwa',
        width: 72,
        render: (v: boolean) => (v ? 'Yes' : 'No'),
      },
      {
        title: 'Shatwa',
        dataIndex: 'shatwa',
        width: 72,
        render: (v: boolean) => (v ? 'Yes' : 'No'),
      },
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 120,
        render: (_, record) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => void openEdit(record)}
            >
              Edit
            </Button>
            <Popconfirm title="Delete this formula?" onConfirm={() => void handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Subanta rup formula
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Declension grid templates (standalone rows, not linked to a subanta). Optional cells use
            JSON keys <Typography.Text code>v_R_C</Typography.Text> for row R and column C (e.g.{' '}
            <Typography.Text code>v_1_1</Typography.Text> … <Typography.Text code>v_8_3</Typography.Text>
            ).
          </Typography.Paragraph>
        </Space>
      </Card>

      <Card
        title="All formulas"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New formula
          </Button>
        }
      >
        <Table<SubantaRupFormula>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 15, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={editing ? `Edit formula #${editing.id}` : 'New subanta rup formula'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        width={960}
        destroyOnClose
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        <Spin spinning={prefillLoading}>
          <Form<SubantaRupFormulaFormValues>
            form={form}
            layout="vertical"
            requiredMark="optional"
            style={{ marginTop: 8 }}
          >
            <Space wrap style={{ width: '100%' }}>
              <Form.Item
                name="prakar"
                label="Prakar"
                rules={[{ required: true, message: 'Select prakar' }]}
                style={{ minWidth: 220, flex: 1 }}
              >
                <Select showSearch optionFilterProp="label" options={prakarOptions} />
              </Form.Item>
              <Form.Item
                name="linga"
                label="Linga"
                rules={[{ required: true, message: 'Select linga' }]}
                style={{ minWidth: 220, flex: 1 }}
              >
                <Select showSearch optionFilterProp="label" options={lingaOptions} />
              </Form.Item>
            </Space>

            <Space wrap style={{ width: '100%' }}>
              <Form.Item name="roop_prakar" label="Roop prakar" style={{ minWidth: 200, flex: 1 }}>
                <Input placeholder="Optional" />
              </Form.Item>
              <Form.Item name="aadhar_shabda" label="Aadhar shabda" style={{ minWidth: 200, flex: 1 }}>
                <Input placeholder="Optional" />
              </Form.Item>
            </Space>

            <Form.Item name="vishesh" label="Vishesh">
              <Input placeholder="Optional" />
            </Form.Item>

            <Form.Item name="bat" label="Bat">
              <Input placeholder="Optional (empty stored as null)" />
            </Form.Item>

            <Space size="large" style={{ marginBottom: 16 }}>
              <Form.Item name="natwa" label="Natwa" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="shatwa" label="Shatwa" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>

            <Typography.Text strong>Declension grid (v_R_C)</Typography.Text>
            <div style={{ marginTop: 8, overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 48, border: '1px solid #f0f0f0', padding: 4 }} />
                    {[1, 2, 3].map((c) => (
                      <th
                        key={c}
                        style={{
                          border: '1px solid #f0f0f0',
                          padding: 6,
                          background: '#fafafa',
                        }}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 8 }, (_, i) => {
                    const r = i + 1;
                    return (
                      <tr key={r}>
                        <td
                          style={{
                            border: '1px solid #f0f0f0',
                            padding: 4,
                            textAlign: 'center',
                            background: '#fafafa',
                            fontWeight: 500,
                          }}
                        >
                          {r}
                        </td>
                        {[1, 2, 3].map((c) => {
                          const name = `v_${r}_${c}` as const;
                          return (
                            <td key={c} style={{ border: '1px solid #f0f0f0', padding: 4 }}>
                              <Form.Item name={name} noStyle>
                                <Input placeholder={name} size="small" />
                              </Form.Item>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Form>
        </Spin>
      </Modal>
    </Space>
  );
}
